/**
 * Login Page
 * 
 * Passwordless authentication with email OTP.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/analytics';

/**
 * Resolve the post-login return path from either router state (set when
 * a component navigates here programmatically) or `?returnTo=…` query
 * param (set when an anonymous /property/:id link triggers sign-in via
 * a plain anchor href). The query-param branch lets the property-file
 * page point back to the signed-in /account/property/:id route after
 * authentication, even across full-page reloads.
 */
function resolveReturnTo(location: ReturnType<typeof useLocation>, params: URLSearchParams): string | undefined {
  const fromState = (location.state as any)?.returnTo;
  if (typeof fromState === 'string' && fromState.startsWith('/')) return fromState;
  const fromPath = (location.state as any)?.from?.pathname;
  if (typeof fromPath === 'string' && fromPath.startsWith('/')) return fromPath;
  const fromQuery = params.get('returnTo');
  // Allow only same-origin paths so a hostile querystring can't redirect off-site.
  if (fromQuery && fromQuery.startsWith('/') && !fromQuery.startsWith('//')) return fromQuery;
  return undefined;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { login, isAuthenticated, error: authError } = useAuth();

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      const returnTo = resolveReturnTo(location, params) || '/account';
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, navigate, location, params]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      trackEvent({ name: 'login_started' });
      const { challengeId } = await login(email);
      const returnTo = resolveReturnTo(location, params);
      navigate('/login/verify', {
        state: { email, challengeId, returnTo },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
      emailRef.current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  // Track login failure after state update
  const displayError = error || authError;

  useEffect(() => {
    if (error) trackEvent({ name: 'login_failed', properties: { reason: error } });
  }, [error]);

  return (
    <>
      <Helmet>
        <title>Sign In | UKPropertyChecks</title>
        <meta name="description" content="Sign in to UKPropertyChecks with email and a one-time code. No password required." />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/login" />
      </Helmet>

      <main className="bg-slate-50 min-h-screen">
        <div className="container-main section-spacing">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold text-slate-950 mb-2">
                Sign in
              </h1>
              <p className="text-slate-600">
                Enter your email to receive a verification code
              </p>
            </div>

            <div className="card p-6">
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-950 mb-1">
                    Email address
                  </label>
                  <input
                    ref={emailRef}
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className={`input ${displayError ? 'border-red-500 border-l-4 border-l-[#dc2626]' : ''}`}
                    style={{ minHeight: '44px' }}
                    placeholder="you@example.com"
                    disabled={isLoading}
                    aria-invalid={displayError ? "true" : "false"}
                    aria-describedby={displayError ? "login-error" : undefined}
                  />
                  <div className="h-6 mt-1">
                    {displayError && (
                      <p id="login-error" className="text-sm font-medium text-red-700" role="alert">{displayError}</p>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="btn-primary w-full"
                >
                  {isLoading ? 'Sending code...' : 'Send verification code'}
                </button>
              </form>
            </div>

            <div className="mt-6 card-subtle p-4">
              <p className="text-sm text-slate-600">
                A 6-digit code will be sent to your email. The code expires in 10 minutes.
                No password required.
              </p>
            </div>

            <div className="mt-6 text-center">
              <a href="/" className="text-emerald-700 hover:text-emerald-800 underline text-sm">
                Back to homepage
              </a>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
